/**
 * Eventos de error compatibles con Sentry (protocolo «envelope») y limpieza
 * de datos sensibles. Es lógica pura: arma la URL y el cuerpo del envío,
 * pero no hace la petición; el Worker y la web usan su propio `fetch`. Así
 * cualquier servicio compatible (Sentry, GlitchTip, Bugsink…) sirve con solo
 * configurar su DSN.
 *
 * Privacidad: a los eventos solo va el id del usuario, nunca nombres, DPI,
 * NIT, teléfonos, correos, contraseñas ni tokens.
 */
/** Texto con el que se reemplaza un dato sensible. */
export const REDACTED = '[redactado]';
/**
 * Fragmentos que delatan un secreto en cualquier parte del nombre de un
 * campo (`password`, `refreshToken`, `x-api-secret`, `Authorization`…).
 */
const SENSITIVE_KEY_FRAGMENTS = ['password', 'passwd', 'token', 'secret', 'authorization', 'cookie', 'apikey', 'api_key'];
/**
 * Palabras completas de un nombre de campo que identifican a una persona o
 * un segundo factor (`cui`, `clientNit`, `phone_number`, `recoveryCode`…).
 * Se comparan por palabra para no confundir `nit` con `unit` o `initial`.
 */
const SENSITIVE_KEY_WORDS = ['cui', 'dpi', 'nit', 'phone', 'telefono', 'mobile', 'email', 'correo', 'otp', 'totp', 'recovery', 'iban', 'account'];
/** Separa un nombre de campo en palabras: camelCase, snake_case, kebab-case o espacios. */
const KEY_WORD_BOUNDARY = /[_\-\s.]+|(?<=[a-z0-9])(?=[A-Z])/;
/**
 * Patrones de datos sensibles dentro de textos libres (mensajes de error,
 * URLs): DPI de 13 dígitos (con o sin espacios), JWT, encabezados Bearer y
 * correos electrónicos.
 */
const SENSITIVE_TEXT_PATTERNS = [
    /\beyJ[\w-]+\.[\w-]+\.[\w-]+/g,
    /\bBearer\s+[\w.~+/=-]+/gi,
    /\b\d{4}\s?\d{5}\s?\d{4}\b/g,
    /[\w.+-]+@[\w-]+(\.[\w-]+)+/g,
];
/** Profundidad máxima que recorre la limpieza (evita ciclos y objetos enormes). */
const MAX_SCRUB_DEPTH = 8;
/** Largo máximo de un texto dentro de un evento (Sentry recorta de todos modos). */
const MAX_TEXT_LENGTH = 4_000;
/** Elementos máximos de un arreglo dentro de un evento. */
const MAX_ARRAY_ITEMS = 50;
/** ¿El nombre del campo sugiere un secreto o un dato personal? */
export function isSensitiveKey(key) {
    const lower = key.toLowerCase();
    if (SENSITIVE_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment)))
        return true;
    const words = key.split(KEY_WORD_BOUNDARY).map((word) => word.toLowerCase());
    return words.some((word) => SENSITIVE_KEY_WORDS.includes(word));
}
/** Reemplaza en un texto libre los DPI, JWT, tokens Bearer y correos. */
export function scrubText(text) {
    const trimmed = text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH)}…` : text;
    return SENSITIVE_TEXT_PATTERNS.reduce((result, pattern) => result.replace(pattern, REDACTED), trimmed);
}
/**
 * Copia de `value` sin datos sensibles: los campos con nombre sensible se
 * reemplazan por `[redactado]` y los textos pasan por `scrubText`. No
 * modifica el original.
 */
export function scrubSensitive(value, depth = 0) {
    if (typeof value === 'string')
        return scrubText(value);
    if (value === null || typeof value !== 'object')
        return value;
    if (depth >= MAX_SCRUB_DEPTH)
        return REDACTED;
    if (Array.isArray(value))
        return value.slice(0, MAX_ARRAY_ITEMS).map((item) => scrubSensitive(item, depth + 1));
    const result = {};
    for (const [key, item] of Object.entries(value)) {
        result[key] = isSensitiveKey(key) ? REDACTED : scrubSensitive(item, depth + 1);
    }
    return result;
}
/** Versión del protocolo de Sentry que se anuncia en la URL. */
const SENTRY_PROTOCOL_VERSION = '7';
/** Identificador del cliente que envía los eventos. */
export const SENTRY_CLIENT_NAME = 'tumin-reporter';
/** Versión del cliente que envía los eventos. */
const SENTRY_CLIENT_VERSION = '1.0.0';
/**
 * Forma de un DSN: protocolo http(s), llave pública, host, subruta opcional y
 * id de proyecto. Se interpreta con una expresión regular porque el dominio
 * no depende de las API del navegador (`URL`).
 */
const DSN_PATTERN = /^(https?:)\/\/([^@/\s:]+)(?::[^@/\s]*)?@([^/\s]+)((?:\/[^/\s]+)*)\/([^/\s?#]+)\/?$/;
/** Interpreta un DSN; devuelve `null` si está vacío o mal formado (el reporte queda apagado). */
export function parseSentryDsn(dsn) {
    if (!dsn)
        return null;
    const match = DSN_PATTERN.exec(dsn.trim());
    if (!match)
        return null;
    const [, protocol, publicKey, host, pathPrefix, projectId] = match;
    if (!protocol || !publicKey || !host || !projectId)
        return null;
    return { protocol, host, pathPrefix: pathPrefix ?? '', publicKey, projectId };
}
/** Origen (`https://host`) al que se envían los eventos; lo necesita la CSP de la web. */
export function sentryOrigin(dsn) {
    return `${dsn.protocol}//${dsn.host}`;
}
/** URL del endpoint «envelope», autenticada con la llave pública en la query. */
export function sentryEnvelopeUrl(dsn) {
    const query = [
        ['sentry_key', dsn.publicKey],
        ['sentry_version', SENTRY_PROTOCOL_VERSION],
        ['sentry_client', `${SENTRY_CLIENT_NAME}/${SENTRY_CLIENT_VERSION}`],
    ]
        .map(([key, value]) => `${key ?? ''}=${encodeURIComponent(value ?? '')}`)
        .join('&');
    return `${sentryOrigin(dsn)}${dsn.pathPrefix}/api/${dsn.projectId}/envelope/?${query}`;
}
/** Línea de pila de V8: `at fn (archivo:línea:columna)` o `at archivo:línea:columna`. */
const V8_FRAME_PATTERN = /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?$/;
/** Línea de pila de Firefox/Safari: `fn@archivo:línea:columna`. */
const GECKO_FRAME_PATTERN = /^(.*)@(.+?):(\d+):(\d+)$/;
/** Cuadros máximos que se envían por excepción. */
const MAX_STACK_FRAMES = 50;
/** Convierte el texto de la pila en cuadros (el más antiguo primero, como espera Sentry). */
export function parseStack(stack) {
    if (!stack)
        return [];
    const frames = [];
    for (const line of stack.split('\n')) {
        const match = V8_FRAME_PATTERN.exec(line) ?? GECKO_FRAME_PATTERN.exec(line);
        if (!match)
            continue;
        const [, fn, filename, lineno, colno] = match;
        if (!filename)
            continue;
        frames.push({
            ...(fn ? { function: fn } : {}),
            filename: scrubText(filename),
            lineno: Number(lineno),
            colno: Number(colno),
            in_app: !filename.includes('node_modules'),
        });
    }
    return frames.slice(0, MAX_STACK_FRAMES).reverse();
}
/** Milisegundos por segundo (Sentry recibe la hora en segundos). */
const MS_PER_SECOND = 1000;
/** Plataforma que se anuncia (el Worker y la web ejecutan JavaScript). */
const EVENT_PLATFORM = 'javascript';
/** Tipo y mensaje de cualquier valor lanzado (no siempre es un `Error`). */
function describeThrown(error) {
    if (error instanceof Error)
        return { type: error.name || 'Error', message: error.message, ...(error.stack ? { stack: error.stack } : {}) };
    // JSON.stringify devuelve undefined para `undefined` y funciones, aunque su tipo diga string.
    const serialized = JSON.stringify(scrubSensitive(error));
    return { type: 'NonError', message: typeof error === 'string' ? error : (serialized ?? String(error)) };
}
/**
 * Arma el evento de Sentry de un error, ya sin datos sensibles.
 * `eventId` son 32 caracteres hexadecimales (un UUID sin guiones).
 */
export function buildErrorEvent(error, context, now, eventId) {
    const thrown = describeThrown(error);
    const frames = parseStack(thrown.stack);
    const tags = { source: context.source, ...context.tags };
    if (context.requestId)
        tags.request_id = context.requestId;
    if (context.route)
        tags.route = context.route;
    return {
        event_id: eventId,
        timestamp: now.getTime() / MS_PER_SECOND,
        platform: EVENT_PLATFORM,
        level: 'error',
        environment: context.environment,
        ...(context.release ? { release: context.release } : {}),
        logger: context.source,
        tags,
        ...(context.userId ? { user: { id: context.userId } } : {}),
        ...(context.route || context.method
            ? { request: { ...(context.method ? { method: context.method } : {}), ...(context.route ? { url: context.route } : {}) } }
            : {}),
        exception: {
            values: [{ type: thrown.type, value: scrubText(thrown.message), ...(frames.length > 0 ? { stacktrace: { frames } } : {}) }],
        },
        extra: scrubSensitive(context.extra ?? {}),
    };
}
/** Cuerpo «envelope»: encabezado, encabezado del ítem y el evento, uno por línea. */
export function buildEnvelope(event, rawDsn, sentAt) {
    const header = { event_id: event.event_id, sent_at: sentAt.toISOString(), dsn: rawDsn, sdk: { name: SENTRY_CLIENT_NAME, version: SENTRY_CLIENT_VERSION } };
    return [JSON.stringify(header), JSON.stringify({ type: 'event' }), JSON.stringify(event)].join('\n');
}
/**
 * Prepara el envío de un error: `null` si no hay DSN (el reporte está
 * apagado). El llamador hace el `fetch` con `method: 'POST'` y este cuerpo.
 */
export function prepareErrorReport(rawDsn, error, context, now, eventId) {
    const dsn = parseSentryDsn(rawDsn);
    if (!dsn || !rawDsn)
        return null;
    const event = buildErrorEvent(error, context, now, eventId);
    return { url: sentryEnvelopeUrl(dsn), body: buildEnvelope(event, rawDsn, now), eventId };
}
//# sourceMappingURL=error-events.js.map